import { useState, useEffect } from 'react';

// Threshold in pixels — DevTools panel typically takes > 160px when docked
const DEVTOOLS_SIZE_THRESHOLD = 160;

// Check interval in milliseconds
const CHECK_INTERVAL_MS = 1000;

/**
 * Returns true if DevTools blocking is enabled.
 * Controlled SOLELY by REACT_APP_DISABLE_DEVTOOLS env var.
 * Set to "true" in .env.production, "false" in .env (dev/UAT).
 */
const isBlockingEnabled = () => {
    return process.env.REACT_APP_DISABLE_DEVTOOLS === 'true';
};

/**
 * Size-based detection: works for DevTools docked to the right or bottom.
 */
const checkDevToolsBySize = () => {
    const widthDiff = window.outerWidth - window.innerWidth;
    const heightDiff = window.outerHeight - window.innerHeight;
    return widthDiff > DEVTOOLS_SIZE_THRESHOLD || heightDiff > DEVTOOLS_SIZE_THRESHOLD;
};

/**
 * Hook that:
 *  - Detects whether DevTools are open (production/configured envs only)
 *  - Blocks F12 / Ctrl+Shift+I / Ctrl+Shift+J / Ctrl+U keyboard shortcuts
 *  - Blocks right-click context menu
 */
const useDevToolsDetection = () => {
    const [isDevToolsOpen, setIsDevToolsOpen] = useState(false);

    useEffect(() => {
        if (!isBlockingEnabled()) return;

        // --- Periodic size check ---
        const runCheck = () => {
            setIsDevToolsOpen(checkDevToolsBySize());
        };

        runCheck();
        const intervalId = setInterval(runCheck, CHECK_INTERVAL_MS);
        window.addEventListener('resize', runCheck);

        // --- Block keyboard shortcuts that open DevTools ---
        const handleKeyDown = (e) => {
            // F12
            if (e.key === 'F12') {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
            // Ctrl+Shift+I  (Chrome / Firefox inspector)
            // Ctrl+Shift+J  (Chrome console)
            // Ctrl+Shift+C  (Chrome element picker)
            // Ctrl+U        (View source)
            if (
                e.ctrlKey &&
                e.shiftKey &&
                (e.key === 'I' || e.key === 'i' || e.key === 'J' || e.key === 'j' || e.key === 'C' || e.key === 'c')
            ) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
            if (e.ctrlKey && (e.key === 'U' || e.key === 'u')) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };

        // --- Block right-click context menu ---
        const handleContextMenu = (e) => {
            e.preventDefault();
            return false;
        };

        document.addEventListener('keydown', handleKeyDown, true);
        document.addEventListener('contextmenu', handleContextMenu, true);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('resize', runCheck);
            document.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('contextmenu', handleContextMenu, true);
        };
    }, []);

    return isDevToolsOpen;
};

export default useDevToolsDetection;
